import React from 'react';

const normalizeDataLoggerId = (dataLoggerId) => {
  if (dataLoggerId == null || dataLoggerId === '') return '';
  if (typeof dataLoggerId === 'object') {
    if (dataLoggerId._id != null && dataLoggerId._id !== '') return String(dataLoggerId._id);
    if (dataLoggerId.id != null && dataLoggerId.id !== '') return String(dataLoggerId.id);
    const asString = String(dataLoggerId);
    return asString === '[object Object]' ? '' : asString;
  }
  return String(dataLoggerId);
};

const findDataLogger = (dataLoggerId, dataLoggers) => {
  const id = normalizeDataLoggerId(dataLoggerId);
  if (!id) return null;
  return dataLoggers.find((dl) => normalizeDataLoggerId(dl._id) === id)
    || dataLoggers.find((dl) => String(dl.serialNo) === id)
    || null;
};

const resolveDataLoggerLabel = (dataLoggerId, dataLoggers) =>
  findDataLogger(dataLoggerId, dataLoggers)?.serialNo || '—';

const resolveDataLoggerLocation = (dataLoggerId, dataLoggers) =>
  findDataLogger(dataLoggerId, dataLoggers)?.location?.trim() || '—';

/** Same grouping as the dashboard sensor list: one block per data logger. */
const dataLoggerGroupKey = (sensor, dataLoggers) => {
  const logger = findDataLogger(sensor?.dataLoggerId, dataLoggers);
  if (logger?.serialNo) return `serial:${String(logger.serialNo)}`;
  const id = normalizeDataLoggerId(sensor?.dataLoggerId);
  return id ? `id:${id}` : '';
};

const consolidateSensorsByDataLogger = (sensors, dataLoggers) => {
  const groups = new Map();
  const order = [];
  sensors.forEach((sensor) => {
    const key = dataLoggerGroupKey(sensor, dataLoggers);
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key).push(sensor);
  });
  return order.flatMap((key) => groups.get(key));
};

const dataLoggerRowSpans = (sensors, dataLoggers) => {
  const spans = new Array(sensors.length).fill(0);
  let i = 0;
  while (i < sensors.length) {
    const key = dataLoggerGroupKey(sensors[i], dataLoggers);
    let j = i + 1;
    while (j < sensors.length && dataLoggerGroupKey(sensors[j], dataLoggers) === key) j += 1;
    spans[i] = j - i;
    i = j;
  }
  return spans;
};

/** Read-only list of sensors linked to a bridge. */
export default function BridgeSensorsReadonly({ sensors = [], dataLoggers = [] }) {
  const activeLoggers = (dataLoggers || []).filter((dl) => dl.isActive !== false);
  const list = consolidateSensorsByDataLogger(sensors || [], activeLoggers);
  const loggerSpans = dataLoggerRowSpans(list, activeLoggers);

  return (
    <div className="staff-section bridge-sensors-readonly">
      <h3 className="staff-section-title">Sensors</h3>
      {list.length === 0 ? (
        <p className="staff-hint">No sensors added yet.</p>
      ) : (
        <div className="bridge-sensors-readonly-scroll">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Span No.</th>
                <th>Girder No.</th>
                <th>Data Logger</th>
                <th>Datalogger Location</th>
                <th>Sensor Type</th>
                <th>Bridge Sensor Sr. No.</th>
                <th>Manual Location</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s, i) => (
                <tr key={s._id || i}>
                  <td>{i + 1}</td>
                  <td>{s.spanNo ?? '—'}</td>
                  <td>{s.girderNo ?? '—'}</td>
                  {loggerSpans[i] > 0 && (
                    <td rowSpan={loggerSpans[i]} className="bridge-sensors-logger-merged">
                      {resolveDataLoggerLabel(s.dataLoggerId, activeLoggers)}
                    </td>
                  )}
                  <td>{resolveDataLoggerLocation(s.dataLoggerId, activeLoggers)}</td>
                  <td>{s.sensorType || '—'}</td>
                  <td>{s.sensorSerialNo || '—'}</td>
                  <td>{s.manualLocation || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {list.length > 0 && (
        <p className="staff-hint">
          {list.length} sensor{list.length !== 1 ? 's' : ''} linked to this bridge
        </p>
      )}
    </div>
  );
}
